import { FormControl, Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Formik, Form as FormikForm } from "formik";
import { UserPlus } from "lucide-react";
import { useHistory, useParams } from "react-router-dom";
import * as yup from "yup";

import { axiosInstance } from "api/request";
import { getUnregisteredData, postRegister } from "api/user";
import { userKeys } from "api/user/queries";
import { RegisterPayloadType } from "api/user/types";
import FormikTextField from "components/forms/FormikTextField";
import UltimateButton from "components/UltimateButton";

const RegisterForm = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const { data: registerData } = useQuery(
    userKeys.register(id),
    async () => {
      const { data: res } = await getUnregisteredData(id);
      return res.data;
    },
    { retry: false, onError: () => history.push("/login") },
  );

  const initialValues = {
    email: registerData?.email || "",
    name: registerData?.name || "",
    password: "",
    repeatPassword: "",
  };

  const schema = yup.object({
    email: yup.string().email("Wrong email format").required(),
    name: yup.string().required(),
    password: yup
      .string()
      .min(3, "Minimum 3 symbols")
      .max(50, "Maximum 50 symbols")
      .required("Password is required"),
    repeatPassword: yup
      .string()
      .required("Password confirmation is required")
      .when("password", {
        is: (val: string | any[]) => (val && val.length > 0 ? true : false),
        then: (schema) =>
          schema.oneOf(
            [yup.ref("password")],
            "Password and Confirm Password didn't match",
          ),
      }),
  });

  const { mutateAsync: register, status } = useMutation(
    async (values: RegisterPayloadType) => {
      const { data: loginData } = await postRegister(values, id);

      localStorage.setItem("JWToken", loginData?.token);
      axiosInstance.defaults.headers.common["Authorization"] =
        `Bearer ${loginData?.token}`;
    },
  );

  const handleSubmit = async (values: RegisterPayloadType) => {
    await register(
      {
        name: values.name,
        password: values.password,
      },
      {
        onSuccess: () => history.push("/credit-evaluations"),
      },
    );
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={handleSubmit}
    >
      <FormikForm>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <Typography mb={0.3}>Email</Typography>
          <FormikTextField name="email" disabled />
        </FormControl>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <Typography mb={0.3}>Name</Typography>
          <FormikTextField name="name" />
        </FormControl>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <Typography mb={0.3}>Password</Typography>
          <FormikTextField name="password" type="password" />
        </FormControl>
        <FormControl fullWidth sx={{ mb: 4 }}>
          <Typography mb={0.3}>Repeat Password</Typography>
          <FormikTextField name="repeatPassword" type="password" />
        </FormControl>

        <UltimateButton
          type="submit"
          loading={status === "loading"}
          text="Register"
          Icon={
            UserPlus as React.ComponentType<{
              size?: number;
              className?: string;
              style?: React.CSSProperties;
            }>
          }
          sx={{ mt: 1, py: 1 }}
        />
      </FormikForm>
    </Formik>
  );
};

export default RegisterForm;
